<template>
  <div
    class="relative inline-block"
    ref="rootRef"
    role="button"
    tabindex="0"
    @click.stop="toggle()"
    @keydown.enter.prevent.stop="toggle()"
    @keydown.space.prevent.stop="toggle()"
    :aria-expanded="open"
    aria-haspopup="menu"
  >
    <slot name="button" :open="open" />
    <teleport to="body">
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="open"
          ref="menuRef"
          class="z-[10010] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto overflow-x-auto outline-none py-1 box-border"
          :style="floatingStyle"
          role="menu"
        >
          <button
            v-for="(item, idx) in items"
            :key="idx"
            type="button"
            class="w-full flex items-center gap-2 px-3 py-1.5 text-md font-normal transition-colors duration-150 focus:outline-none hover:bg-gray-50 focus:bg-[#D0A444]/10"
            :class="
              item.danger
                ? 'text-red-600 hover:bg-red-50 focus:bg-red-50'
                : 'text-gray-700'
            "
            @click="onItem(item)"
          >
            <span
              class="flex-shrink-0 w-4 h-4 flex items-center justify-center"
            >
              <component
                v-if="item.icon"
                :is="item.icon"
                class="w-4 h-4"
                :class="item.danger ? 'text-red-500' : 'text-gray-500'"
              />
            </span>
            <span class="whitespace-nowrap text-left">{{ item.label }}</span>
          </button>
        </div>
      </Transition>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, nextTick, ref, type CSSProperties } from "vue";

type MenuItem = {
  label: string;
  action: () => void;
  icon?: any;
  danger?: boolean;
};

const props = withDefaults(
  defineProps<{
    openOnHover?: boolean;
    align?: "left" | "right";
    items: MenuItem[];
  }>(),
  {
    openOnHover: false,
    align: "left",
  },
);

const rootRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const open = ref(false);
const floatingStyle = ref<CSSProperties>({
  position: "fixed",
  left: "0px",
  top: "0px",
  width: "0px",
});

function position() {
  const el = rootRef.value;
  const menu = menuRef.value;
  if (!el || !menu) return;
  const rect = el.getBoundingClientRect();

  // Viewport constraints
  const vw = window.innerWidth;
  const minWidth = 160; // don't be too narrow
  const maxWidth = Math.min(640, vw - 16); // upper bound (viewport margin)

  // Measure natural content width: temporarily allow auto width then read scrollWidth
  const prevInlineWidth = menu.style.width;
  menu.style.width = "auto";
  // Force reflow/read before measuring
  const measured = Math.ceil(menu.scrollWidth || 0) + 8; // small padding allowance

  // Use content width clamped to viewport bounds
  const desiredWidth = Math.min(Math.max(measured, minWidth), maxWidth);

  let left = props.align === "right" ? rect.right - desiredWidth : rect.left;
  const top = rect.bottom + 6;

  // keep within viewport with 8px margins
  if (left + desiredWidth > vw - 8) left = Math.max(8, vw - desiredWidth - 8);
  if (left < 8) left = 8;

  floatingStyle.value = {
    position: "fixed",
    left: `${left}px`,
    top: `${top}px`,
    width: `${desiredWidth}px`,
  } as CSSProperties;

  // ensure the menu keeps the measured width
  menu.style.width = `${desiredWidth}px`;
}

function onGlobalClick(e: MouseEvent) {
  const t = e.target as Node;
  if (rootRef.value?.contains(t) || menuRef.value?.contains(t)) return;
  toggle(false);
}

function toggle(val?: boolean) {
  const next = val ?? !open.value;
  if (next === open.value) return;
  open.value = next;
  if (open.value) {
    nextTick(() => {
      position();
      document.addEventListener("click", onGlobalClick, true);
      window.addEventListener("scroll", position, true);
      window.addEventListener("resize", position);
    });
  } else {
    document.removeEventListener("click", onGlobalClick, true);
    window.removeEventListener("scroll", position, true);
    window.removeEventListener("resize", position);
  }
}

function onItem(item: MenuItem) {
  try {
    item.action?.();
  } finally {
    toggle(false);
  }
}

onBeforeUnmount(() => toggle(false));

// Expose simple API
defineExpose({ toggle });
</script>

<style scoped></style>
